import { useContext, useEffect, useState } from 'react';
import { KlartextEventConfigContext } from '../providers/klartextEventConfig';
import { useKpiCollection } from '../hooks/useKpiCollection';
import ListeningView from './ListeningView';
import { TakeawayKpiPoint, TakeawayValuePoint } from '@configTypes/kpiTakeaway';
import classNames from 'classnames';
import { KlartextEventConfig } from '@common/types/klartextEventConfig';
import { FrontendSpecificConfig } from '@configTypes/eventConfigs';

type SpeakerTakeaway = {
  speaker: string;
  challenge: string;
  solution: string;
  tools: string;
};

export const TakeawayView = () => {
  const eventConfig = useContext(KlartextEventConfigContext);
  const [takeaways, setTakeaways] = useState<SpeakerTakeaway[]>([]);

  const [takeawayKpis] = useKpiCollection<TakeawayKpiPoint[]>({
    eventId: eventConfig?.eventId ?? null,
    kpiName: 'Takeaway',
    initialState: [] as TakeawayKpiPoint[],
  });

  useEffect(() => {
    // only get the takeaway values of the latest kpi
    const latestTakeaways = takeawayKpis.at(-1)?.value ?? [];
    setTakeaways(latestTakeaways);
  }, [takeawayKpis]);

  if (Object.keys(takeaways).length === 0) {
    return <ListeningView />;
  }

  return (
    <div className="mt-4">
      {takeaways.map((takeaway, index) => (
        <SpeakerTakeaway key={index} speaker={takeaway.speaker} takeaway={takeaway} eventConfig={eventConfig} />
      ))}
    </div>
  );
};

const SpeakerTakeaway = ({
  speaker,
  takeaway,
  eventConfig,
}: {
  speaker: string;
  takeaway: TakeawayValuePoint;
  eventConfig: KlartextEventConfig<FrontendSpecificConfig> | null;
}) => {
  return (
    <div className="mb-4 overflow-hidden rounded-xl bg-eidraWhite">
      <div
        className={classNames(
          'flex justify-between bg-democrat px-5 py-3 text-[0.75rem] text-partyColor',
          speaker === eventConfig?.frontendSpecificConfig?.participants.republican.name
            ? 'bg-republican'
            : 'bg-democrat',
        )}>
        <strong>Takeaway</strong>
        <span>{speaker}</span>
      </div>
      <div className="grid grid-cols-1">
        <TakeawayItem
          title={"America's challenge:"}
          value={takeaway.challenge}
          highlight={true}
          speaker={speaker}
          eventConfig={eventConfig}
        />
        <TakeawayItem
          title={'What can we do about it?'}
          value={takeaway.solution}
          highlight={false}
          speaker={speaker}
          eventConfig={eventConfig}
        />
        <TakeawayItem
          title={'How can we do it?'}
          value={takeaway.tools}
          highlight={false}
          speaker={speaker}
          eventConfig={eventConfig}
        />
      </div>
    </div>
  );
};

const TakeawayItem = ({
  title,
  value,
  highlight,
  speaker,
  eventConfig,
}: {
  title: string;
  value: string;
  highlight: boolean;
  speaker: string;
  eventConfig: KlartextEventConfig<FrontendSpecificConfig> | null;
}) => {
  const highlightClasses = highlight
    ? classNames(
        'after:absolute after:inset-0 after:z-0 after:opacity-40',
        speaker === eventConfig?.frontendSpecificConfig?.participants.republican.name
          ? 'after:bg-republican'
          : 'after:bg-democrat',
      )
    : null;

  return (
    <div
      className={classNames(
        'relative flex flex-col border border-t-0 border-eidraGrey bg-opacity-40 p-5',
        highlightClasses,
      )}>
      <span className="z-10 text-[1.125rem] leading-[1.3]">{title}</span>
      <strong className="z-10 text-[1.5rem] font-medium leading-[1.2]">{value}</strong>
    </div>
  );
};
